// FinTrack — Shared mutable state
// All modules import from here instead of using globals

/**
 * @typedef {Object} AppState
 * @property {Object|null} CU - Current logged-in user
 * @property {Array} vrsteData - Expense types
 * @property {Array} komData - Clients/komitenti
 * @property {Array} usersData - All users (superadmin only)
 * @property {Array} kontniPlan - Chart of accounts mapping
 * @property {boolean} kpLoaded - Whether kontni plan is loaded
 * @property {Array} historyData - Saved report history
 * @property {Object|null} lastReport - Last generated report
 * @property {Array} kasnInvoices - Last kasnjenja invoices
 * @property {number} komPage - Current komitenti page
 * @property {number} kpPage - Current kontni plan page
 */

/** @type {AppState} */
export const state = {
  CU: null,
  vrsteData: [],
  komData: [],
  usersData: [],
  kontniPlan: [],
  kpLoaded: false,
  historyData: [],
  lastReport: null,
  kasnInvoices: [],
  komPage: 0,
  kpPage: 0
};

/** @type {number} Number of items per paginated page */
export const PAGE_SIZE = 50;

/**
 * @typedef {Object} TabDef
 * @property {string} id - Tab identifier used in routing
 * @property {string} label - Display label with emoji prefix
 */

/** @type {TabDef[]} All available navigation tabs */
// SVG ikonice (Lucide stil, 18px)
const _i = (d) => `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${d}</svg>`;

export const ALL_TABS = [
  { id: 'dashboard', label: 'Kontrolna tabla', icon: _i('<rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>') },
  // --- Sekcija: Izveštaji ---
  { id: '_sec_izvestaji', label: 'Izveštaji', section: true },
  { id: 'izvestaj', label: 'Nedeljni izve\u0161taj', icon: _i('<path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>') },
  { id: 'mesecni', label: 'Mese\u010Dni izve\u0161taj', icon: _i('<rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>') },
  { id: 'kasnjenja', label: 'Dani ka\u0161njenja', icon: _i('<circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>') },
  { id: 'komitenti', label: 'Komitenti', icon: _i('<path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/>') },
  { id: 'troskovi', label: 'Vrste tro\u0161kova', icon: _i('<path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/>') },
  // --- Sekcija: Knjiženja ---
  { id: '_sec_knjizenja', label: 'Knjiženja', section: true },
  { id: 'knjizenja', label: 'Knji\u017Eenja', icon: _i('<path d="M4 19.5A2.5 2.5 0 016.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/>') },
  { id: 'kontni-plan', label: 'Kontni plan', icon: _i('<line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>') },
  { id: 'pocetno-stanje', label: 'Po\u010Detno stanje', icon: _i('<line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>') },
  { id: 'zakljucni-list', label: 'Zaklju\u010Dni list', icon: _i('<path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="1"/><line x1="9" y1="12" x2="15" y2="12"/><line x1="9" y1="16" x2="13" y2="16"/>') },
  { id: 'komitenti-full', label: 'Baza komitenata', icon: _i('<path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/>') },
  { id: 'pregled-konta', label: 'Pregled konta', icon: _i('<circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>') },
  // --- Bez sekcije ---
  { id: 'kurs', label: 'Kurs', icon: _i('<line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/>') },
  { id: 'users', label: 'Korisnici', icon: _i('<path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/>') },
  { id: 'istorija', label: 'Istorija', icon: _i('<polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 102.13-9.36L1 10"/>') }
];
